import React, { useState } from 'react';
import {
  ChevronLeft, ClipboardList, CheckCircle2, Clock, AlertTriangle, Star,
  MapPin, Navigation, Map as MapIcon, PhoneCall, X, Camera, FileText,
  ShieldCheck, Lock, Gavel, User
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import {
  v2, statusMap, getStatusTone, getOrderCustomerTotal, getOrderServiceSubtotal,
  getOrderTaxAmount, calculateOrderSettlement, normalizeServiceKey, formatDate,
  ORDER_RENDER_LIMIT, getServiceName, getWorkerPenaltyStatus
} from '../constants';

export default function HistoryView() {
  const {
    lang, t, role, user, userData, orders,
    filterStatus, setFilterStatus, setView, setPreviewImage,
    reviewComment, setReviewComment, rating, setRating,
    workerMaterialsCost, setWorkerMaterialsCost, workerMaterialsNote, setWorkerMaterialsNote,
    currentTime, getWorkerDraftPhotos, setWorkerDraftPhotos,
    handleOrderAction, handleCancelOrder, handleKickWorker, handleDispute,
    grabConfirmOrderId, setGrabConfirmOrderId,
    openWhatsApp, compressImage,
    services, setSelectedService,
    workerMaterialsReceipt, setWorkerMaterialsReceipt,
  } = useAppContext();

  const [cancelConfirmOrder, setCancelConfirmOrder] = useState(null); // 待确认取消的订单
  const [disputeOrder, setDisputeOrder] = useState(null);            // 待提交申诉的订单
  const [disputeReason, setDisputeReason] = useState('');            // 申诉原因

  const workerSkillSet = new Set((userData?.skills || []).map(normalizeServiceKey).filter(Boolean));
  const penalty = role === 'worker' ? getWorkerPenaltyStatus(userData) : { type: 'ok' };
  const penaltyBlocksMarket = penalty.type === 'suspended' || penalty.type === 'cooldown';

  const myOrders = orders.filter(o => {
    if (role === 'client') return o.client_id === user?.uid;
    if (role === 'worker') {
      if (o.techId === user?.uid) return true;
      if (o.status === 'OPEN') {
        if (penaltyBlocksMarket) return false; // 冷却/暂停期间隐藏大厅
        const skillMatch = workerSkillSet.has(normalizeServiceKey(o.service_type));
        const invoiceMatch = !o.invoice_required || (userData?.can_issue_invoice && userData?.invoice_ruc);
        const workerZones = userData?.coverage_zones || [];
        const zoneMatch = workerZones.length === 0 || !o.client_zone || workerZones.includes(o.client_zone);
        return skillMatch && invoiceMatch && zoneMatch;
      }
    }
    return false;
  });

  const filteredOrders = myOrders.filter(o => {
    if (!filterStatus) return true;
    if (filterStatus === 'OPEN') return o.status === 'OPEN';
    if (filterStatus === 'PENDING') return ['AWAITING_VERIFICATION', 'OPEN', 'PENDING_PAYMENT'].includes(o.status);
    if (filterStatus === 'ACTIVE') return ['ACCEPTED', 'ASSIGNED', 'WORKING', 'PENDING_APPROVAL'].includes(o.status);
    return o.status === filterStatus;
  });

  const getOrderSortRank = (order) => {
    if (role !== 'worker') return 0;
    const rank = { ACCEPTED: 0, ASSIGNED: 0, WORKING: 1, PENDING_APPROVAL: 2, DISPUTED: 3, OPEN: 4, COMPLETED: 5, REFUNDED: 6 };
    return rank[order.status] ?? 7;
  };

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const rankDiff = getOrderSortRank(a) - getOrderSortRank(b);
    if (rankDiff !== 0) return rankDiff;
    return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
  });

  const visibleOrders = sortedOrders.slice(0, ORDER_RENDER_LIMIT);
  const hiddenOrderCount = Math.max(0, sortedOrders.length - visibleOrders.length);

  const pendingCount = role === 'worker'
    ? myOrders.filter(o => o.status === 'OPEN').length
    : myOrders.filter(o => ['AWAITING_VERIFICATION', 'OPEN', 'PENDING_PAYMENT'].includes(o.status)).length;
  const activeCount = myOrders.filter(o => ['ACCEPTED', 'ASSIGNED', 'WORKING', 'PENDING_APPROVAL'].includes(o.status)).length;
  const doneCount = myOrders.filter(o => o.status === 'COMPLETED').length;

  const orderTabs = role === 'worker'
    ? [
      { id: null, label: lang === 'es' ? 'Todos' : '全部', count: myOrders.length },
      { id: 'OPEN', label: t.w_p1, count: pendingCount },
      { id: 'ACTIVE', label: t.w_p2, count: activeCount },
      { id: 'COMPLETED', label: t.w_p3, count: doneCount }
    ]
    : [
      { id: null, label: lang === 'es' ? 'Todos' : '全部', count: myOrders.length },
      { id: 'PENDING', label: t.c_p1, count: pendingCount },
      { id: 'ACTIVE', label: t.c_p2, count: activeCount },
      { id: 'COMPLETED', label: t.c_p3, count: doneCount }
    ];

  const getOrderStepText = (order) => {
    const workerText = {
      es: {
        OPEN: 'Trabajo disponible. Revise dirección, fotos y precio antes de tomarlo.',
        ACCEPTED: 'Pedido tomado. Contacte al cliente y suba fotos antes de iniciar.',
        ASSIGNED: 'Pedido asignado. Suba fotos antes de iniciar el trabajo.',
        WORKING: 'Trabajo en proceso. Al terminar, suba fotos finales.',
        PENDING_APPROVAL: 'Trabajo terminado. Esperando aprobación del cliente.',
        COMPLETED: 'Servicio cobrado. Ganancia registrada en su billetera.',
        DISPUTED: 'Pago retenido. Soporte revisará el caso con la evidencia.'
      },
      zh: {
        OPEN: '可接订单。接单前请先确认地址、照片和价格。',
        ACCEPTED: '您已接单。请联系客户，并在开工前上传照片。',
        ASSIGNED: '订单已分配。请先上传开工前照片。',
        WORKING: '服务进行中。完工后请上传完工照片。',
        PENDING_APPROVAL: '已完工，正在等待客户确认验收。',
        COMPLETED: '订单已结算，收入已记录到钱包。',
        DISPUTED: '款项暂时冻结，客服会根据证据介入处理。'
      }
    };
    const text = {
      es: {
        AWAITING_VERIFICATION: 'Pago recibido. Estamos confirmando con Yappy.',
        OPEN: 'Pedido publicado. Esperando que un técnico lo acepte.',
        ACCEPTED: 'Técnico asignado. Verifica su perfil antes de abrir la puerta.',
        WORKING: 'El técnico está trabajando y subiendo evidencia.',
        PENDING_APPROVAL: 'Trabajo terminado. Revisa y aprueba el pago.',
        COMPLETED: 'Servicio finalizado. Garantía FixBox activa.',
        REFUNDED: 'Pedido reembolsado a tu billetera.',
        DISPUTED: 'Caso en revisión por soporte FixBox.'
      },
      zh: {
        AWAITING_VERIFICATION: '已收到支付请求，正在等待 Yappy 确认。',
        OPEN: '订单已发布，正在等待师傅接单。',
        ACCEPTED: '师傅已接单，上门前请核对师傅资料。',
        WORKING: '师傅正在施工，并上传服务证据。',
        PENDING_APPROVAL: '师傅已完工，请检查后确认验收。',
        COMPLETED: '服务已完成，FixBox 售后保障已生效。',
        REFUNDED: '订单已退款至您的钱包。',
        DISPUTED: '订单正在由 FixBox 客服介入处理。'
      }
    };
    if (role === 'worker' && workerText[lang][order.status]) return workerText[lang][order.status];
    return text[lang][order.status] || (lang === 'es' ? 'Estado actualizado.' : '订单状态已更新。');
  };

  const renderOrderInvoiceBox = (order, compact = false) => {
    if (!order) return null;
    const required = Boolean(order.invoice_required);
    const taxAmount = getOrderTaxAmount(order);
    const tone = required
      ? { bg: '#FFF7E6', border: '#FCD34D', color: '#92400E' }
      : { bg: '#F8FAFC', border: '#E3E8EF', color: v2.colors.muted };
    return (
      <div style={{ background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: '8px', padding: compact ? '9px' : '11px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <FileText size={16} color={tone.color} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: '12px', color: tone.color }}>
              {required ? (lang === 'es' ? 'Cliente solicita factura del técnico' : '客户需要师傅开发票') : (lang === 'es' ? 'Sin factura fiscal solicitada' : '客户未要求正式发票')}
            </b>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: tone.color, lineHeight: '1.4', fontWeight: '700' }}>
              {required
                ? (lang === 'es' ? 'FixBox solo emite recibo de servicio; el técnico es el proveedor directo.' : 'FixBox 只提供服务凭证；师傅是实际服务卖方。')
                : (lang === 'es' ? 'FixBox emitirá recibo de servicio, no factura fiscal del trabajo.' : 'FixBox 提供服务凭证，不代替师傅开维修服务发票。')}
            </p>
            {required && (
              <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '10px', color: '#78350F', fontWeight: '800' }}>
                <span>{lang === 'es' ? 'Nombre' : '抬头'}: {order.billing_name || 'N/A'}</span>
                <span>RUC/Ced: {order.billing_tax_id || 'N/A'}{order.billing_dv ? ` DV ${order.billing_dv}` : ''}</span>
                <span style={{ gridColumn: '1 / -1' }}>{lang === 'es' ? 'Correo' : '邮箱'}: {order.billing_email || 'N/A'}</span>
                {taxAmount > 0 && <span style={{ gridColumn: '1 / -1' }}>ITBMS 7%: ${taxAmount.toFixed(2)} {lang === 'es' ? 'cobrado para el técnico' : '由师傅代收处理'}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActionButtons = (order) => {
    if (role === 'worker') {
      const draftPhotos = getWorkerDraftPhotos(order.id);

      const renderWorkerPhotoUploader = (accentColor) => (
        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
          {draftPhotos.map((img, i) => (
            <div key={i} style={{ position: 'relative', minWidth: '62px', height: '62px' }}>
              <img src={img} onClick={() => setPreviewImage(img)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }} alt="p" />
              <X size={14} onClick={() => setWorkerDraftPhotos(order.id, draftPhotos.filter((_, idx) => idx !== i))}
                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '50%', cursor: 'pointer' }} />
            </div>
          ))}
          {draftPhotos.length < 6 && (
            <label style={{ minWidth: '62px', height: '62px', border: `1px dashed ${accentColor}`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', cursor: 'pointer' }}>
              <Camera color={accentColor} size={20} />
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => compressImage(e.target.files[0], d => setWorkerDraftPhotos(order.id, [...draftPhotos, d]))} />
            </label>
          )}
        </div>
      );

      if (order.status === 'OPEN') {
        if (!userData?.isVerified) {
          return (
            <div style={{ marginTop: '14px', padding: '14px', background: v2.colors.softRed, border: '1px solid #FECACA', borderRadius: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Lock size={18} color={v2.colors.red} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <b style={{ display: 'block', color: '#B91C1C', fontSize: '13px', marginBottom: '4px' }}>{t.locked_grab}</b>
                  <p style={{ margin: 0, color: '#7F1D1D', fontSize: '12px', lineHeight: '1.45' }}>
                    {lang === 'es' ? 'Completa documentos, selfie y servicios para que FixBox pueda aprobar tu perfil.' : '请先完善证件、自拍和服务类型，平台审核后才能接单。'}
                  </p>
                </div>
              </div>
              <button onClick={() => setView('editProfile')} style={{ width: '100%', padding: '12px', marginTop: '12px', borderRadius: '8px', backgroundColor: v2.colors.red, color: '#fff', fontWeight: '900', border: 'none', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Completar perfil' : '去完善资料'}
              </button>
            </div>
          );
        }
        const settlementPreview = calculateOrderSettlement(order);
        const needsInvoiceConfirm = order.invoice_required;
        const isConfirming = grabConfirmOrderId === order.id;
        return (
          <div style={{ marginTop: '14px', padding: '14px', background: v2.colors.softBlue, border: `1px solid ${isConfirming ? '#FCD34D' : '#BFDBFE'}`, borderRadius: '8px', transition: 'border-color 0.2s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#1D4ED8', fontWeight: '800' }}>{lang === 'es' ? 'Disponible para tomar' : '可接订单'}</p>
                <b style={{ fontSize: '14px', color: v2.colors.ink }}>{lang === 'es' ? 'Revise datos antes de aceptar' : '接单前请确认服务信息'}</b>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: v2.colors.muted, fontWeight: '700' }}>{lang === 'es' ? 'Tu estimado' : '预计到手'}</p>
                <b style={{ fontSize: '18px', color: v2.colors.green }}>${settlementPreview.workerPayoutTotal.toFixed(2)}</b>
              </div>
            </div>
            {renderOrderInvoiceBox(order, true)}

            {/* 发票二次确认面板（替代 window.confirm） */}
            {isConfirming && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '900', color: '#92400E', fontSize: '13px' }}>
                  {lang === 'es' ? '⚠️ Este pedido requiere factura fiscal del técnico' : '⚠️ 该订单要求师傅开具正式发票'}
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#78350F', lineHeight: '1.5', fontWeight: '600' }}>
                  {lang === 'es'
                    ? 'Al aceptar confirmas que puedes emitir la factura correspondiente al cliente. ¿Deseas continuar?'
                    : '接单即代表您确认具备开票能力并愿意为本单开具正式发票。确认接单吗？'}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              {isConfirming && (
                <button onClick={() => setGrabConfirmOrderId(null)}
                  style={{ flex: 1, padding: '13px', borderRadius: '8px', backgroundColor: v2.colors.softRed, color: '#B91C1C', fontWeight: '900', border: '1px solid #FECACA', cursor: 'pointer', fontSize: '14px' }}>
                  {lang === 'es' ? 'Cancelar' : '取消'}
                </button>
              )}
              <button
                onClick={() => {
                  if (needsInvoiceConfirm && !isConfirming) { setGrabConfirmOrderId(order.id); return; }
                  handleOrderAction(order, order.status);
                }}
                style={{ flex: 2, padding: '14px', borderRadius: '8px', backgroundColor: isConfirming ? '#D97706' : '#2563EB', color: '#fff', fontWeight: '900', border: 'none', fontSize: '15px', cursor: 'pointer', transition: 'background 0.2s' }}>
                {isConfirming ? (lang === 'es' ? '✅ Confirmar y tomar pedido' : '✅ 确认接单') : t.btn_grab}
              </button>
            </div>
          </div>
        );
      }

      if (order.status === 'ACCEPTED' || order.status === 'ASSIGNED') return (
        <div style={{ marginTop: '14px', background: '#F8FAFC', padding: '14px', borderRadius: '8px', border: '1px solid #E3E8EF' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
            <div>
              <p style={{ fontSize: '12px', fontWeight: '800', color: '#1D4ED8', margin: '0 0 4px 0' }}>{lang === 'es' ? 'Paso 1: antes de iniciar' : '第1步：开工前'}</p>
              <b style={{ fontSize: '14px', color: v2.colors.ink }}>{lang === 'es' ? 'Sube al menos 1 foto del estado actual' : '上传至少1张开工前现场照片'}</b>
            </div>
            <span style={{ fontSize: '12px', color: draftPhotos.length > 0 ? v2.colors.green : v2.colors.red, fontWeight: '900' }}>{draftPhotos.length}/6</span>
          </div>
          {renderWorkerPhotoUploader('#2563EB')}
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <button onClick={() => handleCancelOrder(order)} style={{ flex: 1, padding: '12px', borderRadius: '8px', backgroundColor: v2.colors.softRed, color: '#B91C1C', fontWeight: '900', border: '1px solid #FECACA', cursor: 'pointer' }}>
              {lang === 'es' ? 'Abandonar' : '放弃接单'}
            </button>
            <button onClick={() => handleOrderAction(order, order.status)} style={{ flex: 2, padding: '12px', borderRadius: '8px', backgroundColor: draftPhotos.length > 0 ? '#F59E0B' : '#CBD5E1', color: '#fff', fontWeight: '900', border: 'none', cursor: 'pointer' }}>
              {t.btn_start}
            </button>
          </div>
        </div>
      );

      if (order.status === 'WORKING') return (
        <div style={{ marginTop: '14px', background: v2.colors.softGreen, padding: '14px', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
            <div>
              <p style={{ fontSize: '12px', fontWeight: '800', color: '#15803D', margin: '0 0 4px 0' }}>{lang === 'es' ? 'Paso 2: finalizar trabajo' : '第2步：完工确认'}</p>
              <b style={{ fontSize: '14px', color: v2.colors.ink }}>{lang === 'es' ? 'Sube fotos finales antes de cobrar' : '请上传完工照片后提交验收'}</b>
            </div>
            <span style={{ fontSize: '12px', color: draftPhotos.length > 0 ? v2.colors.green : v2.colors.red, fontWeight: '900' }}>{draftPhotos.length}/6</span>
          </div>
          {renderWorkerPhotoUploader('#16A34A')}
          {/* 材料费 */}
          <div style={{ marginTop: '10px', background: '#fff', borderRadius: '8px', padding: '12px', border: '1px solid #D1FAE5' }}>
            <p style={{ fontSize: '12px', fontWeight: '900', color: '#15803D', margin: '0 0 8px 0' }}>
              {lang === 'es' ? '🔧 Materiales (opcional)' : '🔧 材料费（可选）'}
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '700' }}>{lang === 'es' ? 'Costo ($)' : '金额 ($)'}</p>
                <input type="number" min="0" step="0.01" value={workerMaterialsCost}
                  onChange={e => { setWorkerMaterialsCost(e.target.value); if (!e.target.value || parseFloat(e.target.value) <= 0) setWorkerMaterialsReceipt(null); }}
                  placeholder="0.00" style={{ ...v2.field, fontSize: '14px' }} />
              </div>
              <div style={{ flex: 2 }}>
                <p style={{ fontSize: '11px', color: v2.colors.muted, margin: '0 0 4px 0', fontWeight: '700' }}>{lang === 'es' ? 'Detalle' : '说明'}</p>
                <input value={workerMaterialsNote} onChange={e => setWorkerMaterialsNote(e.target.value)}
                  placeholder={lang === 'es' ? 'Ej: tubo PVC, válvula...' : '例：铜管、阀门...'}
                  style={{ ...v2.field, fontSize: '14px' }} />
              </div>
            </div>

            {/* 收据上传：仅当材料费 > 0 时显示 */}
            {parseFloat(workerMaterialsCost) > 0 && (
              <div style={{ marginTop: '8px', padding: '10px', background: '#FFF7E6', borderRadius: '8px', border: `1px solid ${workerMaterialsReceipt ? '#BBF7D0' : '#FCD34D'}` }}>
                <p style={{ fontSize: '11px', fontWeight: '900', color: workerMaterialsReceipt ? '#15803D' : '#B45309', margin: '0 0 8px 0' }}>
                  {workerMaterialsReceipt
                    ? (lang === 'es' ? '✅ Recibo subido' : '✅ 收据已上传')
                    : (lang === 'es' ? '📎 Sube el recibo de materiales (obligatorio)' : '📎 必须上传材料收据照片')}
                </p>
                {workerMaterialsReceipt ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={workerMaterialsReceipt} alt="recibo" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #BBF7D0' }} />
                    <button onClick={() => setWorkerMaterialsReceipt(null)}
                      style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '900', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                      ×
                    </button>
                  </div>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#fff', border: '1.5px dashed #F59E0B', borderRadius: '8px', cursor: 'pointer' }}>
                    <Camera size={18} color="#B45309" />
                    <span style={{ fontSize: '12px', color: '#B45309', fontWeight: '800' }}>
                      {lang === 'es' ? 'Tomar foto del recibo' : '拍摄或上传收据'}
                    </span>
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => compressImage(e.target.files[0], d => setWorkerMaterialsReceipt(d))} />
                  </label>
                )}
              </div>
            )}

            <p style={{ fontSize: '11px', color: '#64748B', margin: '8px 0 0 0', fontWeight: '700' }}>
              {lang === 'es'
                ? '⚠️ Los materiales no pasan por el escrow. Coordina el pago con el cliente directamente.'
                : '⚠️ 材料费不走平台托管，请与客户直接结清。'}
            </p>
          </div>
          <button onClick={() => handleOrderAction(order, order.status)} style={{ width: '100%', padding: '13px', marginTop: '8px', borderRadius: '8px', backgroundColor: draftPhotos.length > 0 ? v2.colors.green : '#CBD5E1', color: '#fff', fontWeight: '900', border: 'none', cursor: 'pointer' }}>
            {t.btn_finish}
          </button>
        </div>
      );

      if (order.status === 'PENDING_APPROVAL') {
        const finishedTime = order.finishedAt?.toDate() || new Date();
        const hoursPassed = (currentTime - finishedTime) / (1000 * 60 * 60);
        const isEligible = hoursPassed >= 8;
        return (
          <div style={{ marginTop: '14px', padding: '14px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E3E8EF' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
              <Clock size={18} color="#B45309" style={{ flexShrink: 0 }} />
              <p style={{ fontSize: '12px', color: '#475569', margin: 0, lineHeight: '1.45', fontWeight: '700' }}>
                {lang === 'es' ? 'Esperando confirmación del cliente. Si no responde en 8 horas, podrá reclamar el pago.' : '正在等待客户确认。完工 8 小时后若客户无异议，可申请自动收款。'}
              </p>
            </div>
            <button disabled={!isEligible} onClick={() => handleOrderAction(order, 'AUTO_CLAIM')}
              style={{ width: '100%', padding: '13px', borderRadius: '8px', backgroundColor: isEligible ? v2.colors.green : '#E2E8F0', color: isEligible ? '#fff' : '#64748B', fontWeight: '900', border: 'none', cursor: isEligible ? 'pointer' : 'not-allowed' }}>
              {t.auto_claim_btn} {!isEligible && ` (${Math.max(0, 8 - hoursPassed).toFixed(1)}h)`}
            </button>
          </div>
        );
      }

      if (order.status === 'DISPUTED') return (
        <div style={{ marginTop: '14px', padding: '14px', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <AlertTriangle size={20} /><b style={{ fontSize: '14px' }}>{lang === 'es' ? 'Pago Retenido' : '资金已被冻结'}</b>
          </div>
          <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 'bold' }}>{t.dispute_reason_label}</p>
          <p style={{ margin: '0 0 10px 0', fontSize: '13px', padding: '10px', background: 'rgba(220,38,38,0.05)', borderRadius: '8px', border: '1px dashed #FECACA', fontStyle: 'italic' }}>"{order.dispute_reason}"</p>
          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>
            {lang === 'es' ? 'El cliente ha abierto una disputa. El equipo de soporte está revisando la situación.' : '请根据客户反馈的原因准备服务照片存证。平台仲裁专员将尽快介入处理。'}
          </p>
        </div>
      );
    }

    if (role === 'client') {
      const renderTechCard = () => {
        if (!order.tech_name && !order.techId) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', background: '#F0F9FF', padding: '15px', borderRadius: '12px', border: '1px solid #BAE6FD', marginBottom: '15px', marginTop: '15px' }}>
            <div style={{ position: 'relative' }}>
              {order.tech_photo
                ? <img src={order.tech_photo} onClick={() => setPreviewImage(order.tech_photo)} style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #0284C7', cursor: 'pointer' }} alt="tech" />
                : <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><User color="#fff" /></div>}
              <ShieldCheck size={18} fill="#fff" color="#34C759" style={{ position: 'absolute', bottom: -2, right: -5, borderRadius: '50%' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 3px 0', fontSize: '14px', fontWeight: 'bold', color: '#0369A1' }}>{order.tech_name || 'Técnico Asignado'}</p>
              <p style={{ margin: 0, fontSize: '10px', color: '#0284C7', lineHeight: '1.3' }}>
                {lang === 'es' ? '🔒 Por seguridad, verifica que esta foto coincida con la persona en tu puerta.' : '🔒 安全提示：请核实上门师傅面容与此照片一致。'}
              </p>
            </div>
            {order.tech_phone && (
              <button onClick={e => { e.stopPropagation(); openWhatsApp(order.tech_phone, lang === 'es' ? `Hola ${order.tech_name}, soy el cliente de FixBox...` : `师傅你好，我是 FixBox 的客户...`); }}
                style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#25D366', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 10px rgba(37,211,102,0.3)' }}>
                <PhoneCall size={18} />
              </button>
            )}
          </div>
        );
      };

      if (order.status === 'PENDING_APPROVAL') return (
        <div style={{ marginTop: '15px', background: '#FFF7ED', padding: '15px', borderRadius: '15px', border: '1px solid #FFEDD5' }}>
          {renderTechCard()}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <div style={{ backgroundColor: '#FF9500', width: '4px', borderRadius: '4px' }}></div>
            <p style={{ fontSize: '13px', color: '#C2410C', margin: 0, lineHeight: '1.6', fontWeight: '500' }}>{t.warm_tip}</p>
          </div>
          {/* 材料费明细 */}
          {order.materials_cost > 0 && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '900', color: '#92400E' }}>
                🔧 {lang === 'es' ? 'Materiales informados por el técnico' : '师傅申报材料费'}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#78350F', fontWeight: '700' }}>{order.materials_note || (lang === 'es' ? 'Sin detalle' : '无说明')}</span>
                <b style={{ fontSize: '18px', color: '#B45309' }}>${(order.materials_cost).toFixed(2)}</b>
              </div>
              {/* 收据照片 */}
              {order.materials_receipt_photo && (
                <div style={{ marginBottom: '8px' }}>
                  <p style={{ fontSize: '11px', color: '#92400E', fontWeight: '900', margin: '0 0 5px 0' }}>
                    📎 {lang === 'es' ? 'Recibo del técnico:' : '师傅收据：'}
                  </p>
                  <img
                    src={order.materials_receipt_photo}
                    alt="recibo materiales"
                    onClick={() => setPreviewImage(order.materials_receipt_photo)}
                    style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #FCD34D', cursor: 'pointer' }}
                  />
                </div>
              )}
              <p style={{ margin: 0, fontSize: '11px', color: '#92400E', fontWeight: '700' }}>
                {lang === 'es'
                  ? '⚠️ Este monto no está incluido en el pago por App. Coordina directamente con el técnico.'
                  : '⚠️ 此费用不含在平台付款中，请直接与师傅核实结清。'}
              </p>
            </div>
          )}
          <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', margin: '0 0 10px 0' }}>
            {lang === 'es' ? 'Califica al Técnico:' : '评价师傅 (Califica al Técnico):'}
          </p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
            {[1, 2, 3, 4, 5].map(star => (
              <Star key={star} size={28} fill={star <= rating ? '#FF9500' : 'none'} color={star <= rating ? '#FF9500' : '#ccc'} onClick={() => setRating(star)} style={{ cursor: 'pointer' }} />
            ))}
          </div>
          <textarea placeholder={lang === 'es' ? 'Escribe un comentario (Opcional)...' : '写下您的评价 (选填)...'} value={reviewComment} onChange={e => setReviewComment(e.target.value)}
            style={{ width: '100%', height: '80px', padding: '12px', borderRadius: '10px', border: '1px solid #ddd', boxSizing: 'border-box', outline: 'none', fontSize: '14px', marginBottom: '10px' }} />
          <button onClick={() => handleOrderAction(order, order.status)} style={{ width: '100%', padding: '15px', borderRadius: '10px', backgroundColor: '#34C759', color: '#fff', fontWeight: 'bold', border: 'none', fontSize: '16px' }}>
            {t.btn_approve}
          </button>
          <button onClick={() => { setDisputeOrder(order); setDisputeReason(''); }} style={{ width: '100%', padding: '15px', borderRadius: '10px', backgroundColor: 'transparent', color: '#EF4444', fontWeight: 'bold', border: '1px solid #EF4444', fontSize: '14px', marginTop: '10px' }}>
            {t.btn_dispute}
          </button>
        </div>
      );

      if (order.status === 'OPEN') return (
        <button onClick={() => setCancelConfirmOrder(order)} style={{ width: '100%', padding: '12px', marginTop: '15px', borderRadius: '10px', backgroundColor: '#FFE4E6', color: '#DC2626', fontWeight: 'bold', border: 'none', fontSize: '14px', cursor: 'pointer' }}>
          {lang === 'es' ? 'Cancelar y Reembolsar' : '取消订单并全额退款'}
        </button>
      );

      if (order.status === 'ACCEPTED' || order.status === 'WORKING') {
        const acceptedTime = order.acceptedAt?.toDate() || new Date();
        const minutesPassed = (currentTime - acceptedTime) / (1000 * 60);
        const isKickEligible = minutesPassed >= 60;
        return (
          <div style={{ marginTop: '15px', background: '#FFFBEB', padding: '15px', borderRadius: '15px', border: '1px solid #FEF3C7' }}>
            {renderTechCard()}
            <p style={{ fontSize: '12px', color: '#D97706', margin: '0 0 10px 0', lineHeight: '1.5' }}>
              {lang === 'es' ? 'Si el técnico demora más de 60 mins sin respuesta, puedes buscar otro o cancelar.' : '师傅接单后将尽快赶来。若超过 60 分钟师傅无响应，您可以重新派单或取消。'}
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setCancelConfirmOrder(order)} style={{ flex: 1, padding: '10px', borderRadius: '8px', backgroundColor: '#FFE4E6', color: '#DC2626', fontWeight: 'bold', border: 'none', fontSize: '12px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Cancelar y Reembolsar' : '取消并退款'}
              </button>
              <button disabled={!isKickEligible} onClick={() => handleKickWorker(order)}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', backgroundColor: isKickEligible ? '#F59E0B' : '#E5E7EB', color: isKickEligible ? '#fff' : '#999', fontWeight: 'bold', border: 'none', fontSize: '12px', cursor: isKickEligible ? 'pointer' : 'not-allowed' }}>
                {lang === 'es' ? 'Cambiar Técnico' : '踢掉重发'} {!isKickEligible && ` (${Math.max(0, 60 - minutesPassed).toFixed(0)}m)`}
              </button>
            </div>
          </div>
        );
      }

      if (order.status === 'DISPUTED') return (
        <div style={{ marginTop: '15px', padding: '15px', borderRadius: '10px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
            <Gavel size={20} /><b style={{ fontSize: '14px' }}>{lang === 'es' ? 'Disputa Abierta' : '申诉处理中'}</b>
          </div>
          <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5' }}>
            {lang === 'es' ? 'El dinero está seguro. Soporte se contactará contigo pronto.' : '资金已安全冻结。平台仲裁专员将尽快审核您的问题。'}
          </p>
        </div>
      );

      if (order.status === 'COMPLETED') {
        const reorderService = services?.find(s =>
          (s.service_name || '').toLowerCase() === (order.service_type || '').toLowerCase()
        ) || services?.[0];

        const handlePrintReceipt = () => {
          const clientTotal = getOrderCustomerTotal(order);
          const serviceSubtotal = getOrderServiceSubtotal(order);
          const taxAmount = getOrderTaxAmount(order);
          const workerEarnings = Number(order.worker_earnings || 0);
          const platformFee = Number(order.platform_fee || 0);
          const completedDate = order.completedAt?.toDate?.() || order.approvedAt?.toDate?.() || new Date();
          const dateStr = completedDate.toLocaleDateString('es-PA', { year: 'numeric', month: 'long', day: 'numeric' });
          const timeStr = completedDate.toLocaleTimeString('es-PA', { hour: '2-digit', minute: '2-digit' });

          const html = `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>FixBox ${lang === 'es' ? 'Recibo' : '服务凭证'} — ${order.order_id}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 360px; margin: 0 auto; padding: 24px 20px; color: #17202A; }
  .logo { font-size: 22px; font-weight: 900; color: #2563EB; letter-spacing: -0.5px; margin-bottom: 4px; }
  .subtitle { font-size: 11px; color: #697586; margin-bottom: 18px; }
  .badge { display: inline-block; background: #ECFDF3; color: #15803D; border: 1px solid #BBF7D0; border-radius: 6px; padding: 4px 10px; font-size: 11px; font-weight: 900; margin-bottom: 16px; }
  h2 { font-size: 14px; color: #697586; font-weight: 700; margin-bottom: 6px; margin-top: 16px; text-transform: uppercase; letter-spacing: 0.5px; }
  .info-row { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid #F1F5F9; font-size: 13px; }
  .info-row:last-child { border-bottom: none; }
  .label { color: #697586; }
  .value { font-weight: 700; text-align: right; }
  .total-row { display: flex; justify-content: space-between; padding: 10px 0; font-size: 16px; font-weight: 900; border-top: 2px solid #17202A; margin-top: 8px; }
  .divider { border: none; border-top: 1px dashed #CBD5E1; margin: 14px 0; }
  .footer { font-size: 11px; color: #94A3B8; text-align: center; margin-top: 22px; line-height: 1.6; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="logo">FixBox</div>
  <div class="subtitle">${lang === 'es' ? 'Plataforma de servicios del hogar · Panamá' : '巴拿马专业上门维修平台'}</div>
  <div class="badge">✅ ${lang === 'es' ? 'SERVICIO COMPLETADO' : '服务已完成'}</div>

  <h2>${lang === 'es' ? 'Datos del pedido' : '订单信息'}</h2>
  <div class="info-row"><span class="label">${lang === 'es' ? 'N° Pedido' : '订单号'}</span><span class="value">${order.order_id}</span></div>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Servicio' : '服务项目'}</span><span class="value">${order.service_type || '—'}</span></div>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Técnico' : '师傅'}</span><span class="value">${order.tech_name || '—'}</span></div>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Cliente' : '客户'}</span><span class="value">${order.client_no || '—'}</span></div>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Dirección' : '服务地址'}</span><span class="value" style="max-width:190px;word-break:break-word;text-align:right">${order.address || '—'}</span></div>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Fecha' : '完成时间'}</span><span class="value">${dateStr} ${timeStr}</span></div>

  <hr class="divider" />
  <h2>${lang === 'es' ? 'Desglose de pago' : '费用明细'}</h2>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Subtotal servicio' : '服务小计'}</span><span class="value">$${serviceSubtotal.toFixed(2)}</span></div>
  ${taxAmount > 0 ? `<div class="info-row"><span class="label">ITBMS 7%</span><span class="value">+$${taxAmount.toFixed(2)}</span></div>` : ''}
  ${order.discount_amount > 0 ? `<div class="info-row"><span class="label">${lang === 'es' ? 'Descuento' : '优惠折扣'}</span><span class="value" style="color:#15803D">-$${Number(order.discount_amount).toFixed(2)}</span></div>` : ''}
  <div class="total-row"><span>${lang === 'es' ? 'TOTAL PAGADO' : '实付总额'}</span><span>$${clientTotal.toFixed(2)}</span></div>

  ${role === 'worker' && workerEarnings > 0 ? `<hr class="divider" />
  <h2>${lang === 'es' ? 'Tu ingreso' : '师傅结算'}</h2>
  <div class="info-row"><span class="label">${lang === 'es' ? 'Comisión FixBox 10%' : '平台服务费 10%'}</span><span class="value">-$${platformFee.toFixed(2)}</span></div>
  <div class="total-row"><span>${lang === 'es' ? 'GANANCIA NETA' : '到手金额'}</span><span style="color:#15803D">$${workerEarnings.toFixed(2)}</span></div>` : ''}

  <div class="footer">
    <p>🛡️ ${lang === 'es' ? 'Garantía FixBox · 30 días · Desde ' : 'FixBox售后保障 · 30天 · 完成于 '}${dateStr}</p>
    <p style="margin-top:6px">fixboxpa.com · WhatsApp +507 6613-1328</p>
    <p style="margin-top:6px">${lang === 'es' ? 'Este recibo es comprobante de pago y uso del servicio FixBox.' : '本凭证为 FixBox 平台服务付款及完成证明。'}</p>
  </div>
</body>
</html>`;
          const w = window.open('', '_blank', 'width=420,height=700');
          w.document.write(html);
          w.document.close();
          setTimeout(() => { w.focus(); w.print(); }, 300);
        };

        return (
          <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={handlePrintReceipt}
              style={{ width: '100%', padding: '11px', borderRadius: '10px', backgroundColor: '#F0FDF4', color: '#15803D', fontWeight: '900', border: '1px solid #BBF7D0', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              🖨️ {lang === 'es' ? 'Imprimir recibo / Guardar PDF' : '打印收据 / 保存PDF'}
            </button>
            <button
              onClick={() => {
                if (reorderService) setSelectedService(reorderService);
                setView('home');
              }}
              style={{ width: '100%', padding: '11px', borderRadius: '10px', backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: '900', border: '1px solid #BFDBFE', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              🔄 {lang === 'es' ? 'Volver a pedir el mismo servicio' : '再次预约同款服务'}
            </button>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div style={{ padding: '14px 16px 24px' }}>
      {/* 页头 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <button onClick={() => { setView('profile'); setFilterStatus(null); }}
          style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #D8DEE8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <p style={{ margin: 0, color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Centro de pedidos' : '订单中心'}</p>
          <h2 style={{ fontWeight: '900', margin: 0, color: v2.colors.ink, fontSize: '21px' }}>{t.orders}</h2>
        </div>
      </div>

      {/* 师傅弃单惩罚横幅 */}
      {role === 'worker' && penalty.type !== 'ok' && (
        <div style={{
          borderRadius: '10px', padding: '13px 15px', marginBottom: '14px',
          background: penalty.type === 'suspended' ? '#FEF2F2' : penalty.type === 'cooldown' ? '#FFF7ED' : '#FFFBEB',
          border: `1px solid ${penalty.type === 'suspended' ? '#FECACA' : penalty.type === 'cooldown' ? '#FFEDD5' : '#FEF3C7'}`
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '900', fontSize: '13px',
            color: penalty.type === 'suspended' ? '#B91C1C' : penalty.type === 'cooldown' ? '#C2410C' : '#92400E' }}>
            {penalty.type === 'suspended' && (lang === 'es' ? '⛔ Cuenta suspendida — Contacta soporte FixBox' : '⛔ 账号已暂停，请联系 FixBox 客服')}
            {penalty.type === 'cooldown' && (lang === 'es' ? `⏳ Periodo de enfriamiento — ${penalty.remaining}h restantes` : `⏳ 冷却期中 — 还剩 ${penalty.remaining} 小时`)}
            {penalty.type === 'warning' && (lang === 'es' ? `⚠️ Advertencia: ${penalty.count} pedidos abandonados` : `⚠️ 警告：已弃单 ${penalty.count} 次`)}
          </p>
          <p style={{ margin: 0, fontSize: '12px', fontWeight: '600',
            color: penalty.type === 'suspended' ? '#7F1D1D' : penalty.type === 'cooldown' ? '#7C2D12' : '#78350F', lineHeight: '1.5' }}>
            {penalty.type === 'suspended' && (lang === 'es' ? 'Has superado el límite (10 abandonos). No puedes tomar pedidos hasta que soporte reactive tu cuenta.' : '您的弃单次数已达上限（10次），需要客服手动解封后方可接单。')}
            {penalty.type === 'cooldown' && (lang === 'es' ? 'Por abandono frecuente, el mercado está bloqueado temporalmente. Se reactiva automáticamente.' : '由于频繁弃单，大厅已临时屏蔽。冷却期过后自动恢复。')}
            {penalty.type === 'warning' && (lang === 'es' ? '5 abandonos → bloqueo 24h · 10 abandonos → suspensión de cuenta.' : '累计5次弃单→封禁24小时；10次→账号暂停，需客服解封。')}
          </p>
        </div>
      )}

      {/* 状态筛选标签 */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '8px' }}>
        {orderTabs.map(tab => (
          <button key={tab.id || 'all'} onClick={() => setFilterStatus(tab.id)}
            style={{ padding: '9px 12px', borderRadius: '10px', border: `1px solid ${filterStatus === tab.id ? '#2962FF' : '#E8ECF0'}`, background: filterStatus === tab.id ? '#2962FF' : '#fff', color: filterStatus === tab.id ? '#fff' : '#374151', fontSize: '12px', fontWeight: '800', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            {tab.label} <span style={{ opacity: filterStatus === tab.id ? 0.82 : 0.55 }}>({tab.count})</span>
          </button>
        ))}
      </div>

      {/* 空状态 */}
      {visibleOrders.length === 0 && (
        <div style={{ ...v2.card, padding: '28px 18px', textAlign: 'center' }}>
          <ClipboardList size={34} color="#94A3B8" style={{ margin: '0 auto 10px' }} />
          <b style={{ display: 'block', color: v2.colors.ink, marginBottom: '6px' }}>{lang === 'es' ? 'No hay pedidos aquí' : '这里暂无订单'}</b>
          <p style={{ margin: 0, color: v2.colors.muted, fontSize: '13px', lineHeight: '1.5' }}>
            {lang === 'es' ? 'Cuando tengas pedidos en este estado, aparecerán en esta lista.' : '当有对应状态的订单时，会显示在这里。'}
          </p>
        </div>
      )}

      {/* 订单列表 */}
      {visibleOrders.map(o => {
        const tone = getStatusTone(o.status);
        const orderAmount = getOrderCustomerTotal(o);
        const serviceSubtotal = getOrderServiceSubtotal(o);
        const taxAmount = getOrderTaxAmount(o);
        const settlementPreview = calculateOrderSettlement(o);
        const workerNet = parseFloat(o.worker_earnings ?? settlementPreview.workerPayoutTotal);
        const showWorkerNet = role === 'worker' && !['REFUNDED', 'DISPUTED'].includes(o.status);
        const workerStepIndex = o.status === 'COMPLETED' ? 3 : (o.status === 'PENDING_APPROVAL' ? 2 : (o.status === 'WORKING' ? 1 : (['ACCEPTED', 'ASSIGNED'].includes(o.status) ? 0 : -1)));
        const workerSteps = [lang === 'es' ? 'Tomado' : '已接单', lang === 'es' ? 'En trabajo' : '施工中', lang === 'es' ? 'Por aprobar' : '待验收', lang === 'es' ? 'Cobrado' : '已结算'];
        const showClientContact = role === 'worker' && o.techId === user?.uid && o.status !== 'OPEN';

        return (
          <div key={o.id} style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
            {/* 订单头 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <b style={{ fontSize: '14px', color: v2.colors.ink }}>{o.order_id}</b>
                <p style={{ fontSize: '11px', color: v2.colors.muted, margin: '4px 0 0 0' }}>{formatDate(o.createdAt)}</p>
              </div>
              <span style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}`, borderRadius: '8px', padding: '6px 8px', fontWeight: '900', fontSize: '11px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {statusMap[lang][o.status] || o.status}
              </span>
            </div>

            {/* 状态说明 */}
            <div style={{ background: '#F8FAFC', border: '1px solid #EEF2F6', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
              <p style={{ margin: '0 0 5px 0', color: v2.colors.muted, fontSize: '11px', fontWeight: '800' }}>{lang === 'es' ? 'Estado actual' : '当前进度'}</p>
              <p style={{ margin: 0, color: v2.colors.ink, fontSize: '13px', lineHeight: '1.45', fontWeight: '700' }}>{getOrderStepText(o)}</p>
            </div>

            {/* 师傅进度步骤 */}
            {role === 'worker' && workerStepIndex >= 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '12px' }}>
                {workerSteps.map((label, idx) => {
                  const isDone = idx <= workerStepIndex;
                  return (
                    <div key={label} style={{ minHeight: '54px', borderRadius: '8px', border: `1px solid ${isDone ? '#BBF7D0' : '#E3E8EF'}`, background: isDone ? v2.colors.softGreen : '#F8FAFC', padding: '8px 5px', textAlign: 'center', boxSizing: 'border-box' }}>
                      {isDone ? <CheckCircle2 size={15} color={v2.colors.green} style={{ margin: '0 auto 4px' }} /> : <Clock size={15} color="#94A3B8" style={{ margin: '0 auto 4px' }} />}
                      <p style={{ margin: 0, fontSize: '10px', color: isDone ? '#166534' : v2.colors.muted, fontWeight: '900', lineHeight: '1.15' }}>{label}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 服务名 & 金额 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '16px', color: v2.colors.ink }}>
                  <b>{getServiceName(o.service_type, lang)} {o.quantity > 1 ? <span style={{ color: v2.colors.gold }}>x {o.quantity}</span> : ''}</b>
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: v2.colors.muted }}>
                  {o.pay_method === 'wallet' ? (lang === 'es' ? 'Pagado con billetera' : '钱包支付') : (o.pay_method === 'yappy' ? 'Yappy' : (lang === 'es' ? 'Método pendiente' : '支付方式待确认'))}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                {o.discount_amount > 0 && <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#94A3B8', textDecoration: 'line-through' }}>${parseFloat(o.base_amount || 0).toFixed(2)}</p>}
                {showWorkerNet ? (
                  <>
                    <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: v2.colors.muted, fontWeight: '800' }}>{o.status === 'COMPLETED' ? (lang === 'es' ? 'Ganancia' : '已到手') : (lang === 'es' ? 'Tu estimado' : '预计到手')}</p>
                    <b style={{ fontSize: '20px', color: v2.colors.green }}>${workerNet.toFixed(2)}</b>
                    <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: '#94A3B8' }}>{lang === 'es' ? 'Total' : '订单'} ${orderAmount.toFixed(2)}</p>
                  </>
                ) : (
                  <b style={{ fontSize: '20px', color: v2.colors.green }}>${orderAmount.toFixed(2)}</b>
                )}
              </div>
            </div>

            {renderOrderInvoiceBox(o)}

            {/* 客户退款通知 */}
            {role === 'client' && o.refunded_to_client > 0 && (
              <div style={{ background: v2.colors.softRed, border: '1px solid #FECACA', borderRadius: '8px', padding: '10px', color: '#B91C1C', fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>
                {lang === 'es' ? `Reembolso/compensación: $${parseFloat(o.refunded_to_client).toFixed(2)}` : `退款/补偿金额：$${parseFloat(o.refunded_to_client).toFixed(2)}`}
              </div>
            )}

            {/* 师傅结算明细 */}
            {role === 'worker' && o.status === 'COMPLETED' && o.worker_earnings != null && (
              <div style={{ background: v2.colors.softGold, padding: '12px', borderRadius: '8px', marginBottom: '10px', border: '1px solid #FCD34D', fontSize: '12px', color: '#92400E' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span>{lang === 'es' ? 'Precio del servicio' : '服务本金'}:</span><span>${serviceSubtotal.toFixed(2)}</span></div>
                {taxAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#B45309' }}><span>ITBMS 7%:</span><span>+${taxAmount.toFixed(2)}</span></div>}
                {o.refunded_to_client > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#B91C1C' }}><span>{lang === 'es' ? 'Compensación por queja' : '客诉扣款补偿'}:</span><span>-${parseFloat(o.refunded_to_client).toFixed(2)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span>{lang === 'es' ? 'Comisión FixBox (10%)' : '平台服务费 (10%)'}:</span><span>-${parseFloat(o.platform_fee || 0).toFixed(2)}</span></div>
                {taxAmount > 0 && <div style={{ fontSize: '10px', lineHeight: '1.35', margin: '6px 0', color: '#854D0E' }}>{lang === 'es' ? 'El ITBMS fue cobrado al cliente para ser manejado por el técnico según su obligación fiscal.' : 'ITBMS 是向客户收取并由师傅按自身税务义务处理的税款。'}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px dashed #FCD34D', paddingTop: '8px', marginTop: '4px', fontSize: '14px' }}>
                  <span>{lang === 'es' ? 'Total recibido' : '师傅钱包入账'}:</span>
                  <span style={{ color: '#B45309' }}>${parseFloat(o.worker_earnings || 0).toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* 评分 */}
            {o.status === 'COMPLETED' && o.rating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '10px' }}>
                {[1, 2, 3, 4, 5].map(star => <Star key={star} size={14} fill={star <= o.rating ? '#FF9500' : 'none'} color={star <= o.rating ? '#FF9500' : '#ddd'} />)}
              </div>
            )}

            {/* 地址 & 联系 */}
            <div style={{ backgroundColor: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #EEF2F6' }}>
              {showClientContact && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', background: '#FFFFFF', border: '1px solid #E3E8EF', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px 0', fontSize: '10px', color: v2.colors.muted, fontWeight: '900' }}>{lang === 'es' ? 'Cliente' : '客户'}</p>
                    <b style={{ fontSize: '13px', color: v2.colors.ink, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.client_no || (lang === 'es' ? 'Cliente FixBox' : 'FixBox客户')}</b>
                  </div>
                  {o.client_phone ? (
                    <button onClick={e => { e.stopPropagation(); openWhatsApp(o.client_phone, lang === 'es' ? `Hola, soy el técnico de FixBox para el pedido ${o.order_id}.` : `您好，我是 FixBox 订单 ${o.order_id} 的师傅。`); }}
                      style={{ width: '38px', height: '38px', borderRadius: '8px', background: '#25D366', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                      <PhoneCall size={17} />
                    </button>
                  ) : (
                    <span style={{ fontSize: '11px', color: v2.colors.muted, fontWeight: '800' }}>{lang === 'es' ? 'Sin teléfono' : '暂无电话'}</span>
                  )}
                </div>
              )}
              {/* 地区 + 紧急/预约标签 */}
              {(o.client_zone_label || o.is_urgent || o.schedule_slot) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {o.client_zone_label && (
                    <span style={{ fontSize: '11px', fontWeight: '800', background: '#EBF5FF', color: '#0284C7', borderRadius: '20px', padding: '4px 9px' }}>
                      📍 {o.client_zone_label}
                    </span>
                  )}
                  {o.is_urgent && (
                    <span style={{ fontSize: '11px', fontWeight: '800', background: '#FEF2F2', color: '#DC2626', borderRadius: '20px', padding: '4px 9px' }}>
                      🚨 {lang === 'es' ? 'Urgente' : '紧急'}
                    </span>
                  )}
                  {!o.is_urgent && o.schedule_day && (
                    <span style={{ fontSize: '11px', fontWeight: '800', background: '#F0FDF4', color: '#15803D', borderRadius: '20px', padding: '4px 9px' }}>
                      🕐 {o.schedule_day === 'today' ? (lang === 'es' ? 'Hoy' : '今天') : o.schedule_day === 'tomorrow' ? (lang === 'es' ? 'Mañana' : '明天') : o.schedule_date || o.schedule_day}
                      {o.schedule_slot ? ` · ${o.schedule_slot === 'morning' ? '8-12h' : o.schedule_slot === 'afternoon' ? '12-17h' : '17-20h'}` : ''}
                    </span>
                  )}
                </div>
              )}
              <p style={{ fontSize: '12px', color: '#334155', margin: '0 0 8px 0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <MapPin size={14} color={v2.colors.ink} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ lineHeight: '1.4' }}>{o.address || 'Ciudad de Panamá'}</span>
              </p>
              {role === 'worker' && o.address && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', marginLeft: '20px' }}>
                  <button onClick={e => { e.stopPropagation(); window.open(`https://waze.com/ul?q=${encodeURIComponent(o.address)}`, '_blank'); }}
                    style={{ padding: '7px 10px', background: '#E0F2FE', color: '#0284C7', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Navigation size={12} /> Waze
                  </button>
                  <button onClick={e => { e.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.address)}`, '_blank'); }}
                    style={{ padding: '7px 10px', background: '#DCFCE7', color: '#15803D', border: 'none', borderRadius: '8px', fontSize: '11px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapIcon size={12} /> Maps
                  </button>
                </div>
              )}
              {o.client_message && <p style={{ fontSize: '12px', color: '#334155', margin: '0 0 8px 0', lineHeight: '1.4' }}>{o.client_message}</p>}
              {o.materials_cost > 0 && (
                <p style={{ fontSize: '12px', color: '#B45309', margin: '0 0 8px 0', fontWeight: '800' }}>
                  🔧 {lang === 'es' ? 'Materiales' : '材料费'}: ${o.materials_cost.toFixed(2)}{o.materials_note ? ` · ${o.materials_note}` : ''}
                </p>
              )}
              {o.review_comment && <p style={{ fontSize: '12px', color: '#B45309', margin: '0 0 8px 0', fontStyle: 'italic' }}>{lang === 'es' ? 'Reseña' : '评价'}: {o.review_comment}</p>}
              {o.client_photos && o.client_photos.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto' }}>
                  {o.client_photos.map((img, i) => <img key={i} src={img} onClick={() => setPreviewImage(img)} style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #D8DEE8', cursor: 'pointer' }} alt="c_photo" />)}
                </div>
              )}
            </div>

            {renderActionButtons(o)}
          </div>
        );
      })}

      {hiddenOrderCount > 0 && (
        <div style={{ ...v2.card, padding: '14px', textAlign: 'center', color: v2.colors.muted, fontSize: '12px', fontWeight: '800' }}>
          {lang === 'es' ? `Mostrando los primeros ${ORDER_RENDER_LIMIT}. Usa los filtros para ver menos pedidos.` : `已显示前 ${ORDER_RENDER_LIMIT} 条。可使用筛选减少列表数量。`}
        </div>
      )}

      {/* ── 取消订单确认模态框 ── */}
      {cancelConfirmOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '900', color: '#DC2626' }}>
              {lang === 'es' ? '¿Cancelar pedido?' : '确认取消订单？'}
            </p>
            <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
              {cancelConfirmOrder.order_id} · ${getOrderCustomerTotal(cancelConfirmOrder).toFixed(2)}
            </p>
            {['ACCEPTED', 'WORKING'].includes(cancelConfirmOrder.status) && (
              <p style={{ margin: '0 0 18px 0', fontSize: '12px', color: '#B45309', background: '#FFF7E6', border: '1px solid #FDE68A', borderRadius: '8px', padding: '10px', lineHeight: '1.5' }}>
                ⚠️ {lang === 'es'
                  ? 'El técnico ya aceptó tu pedido. Se le notificará de la cancelación.'
                  : '师傅已接单，取消后将收到通知。'}
              </p>
            )}
            {!['ACCEPTED', 'WORKING'].includes(cancelConfirmOrder.status) && (
              <p style={{ margin: '0 0 18px 0', fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
                {lang === 'es'
                  ? 'El dinero se devuelve al instante a tu billetera FixBox.'
                  : '全额立即退回您的FixBox钱包余额。'}
              </p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                onClick={() => setCancelConfirmOrder(null)}
                style={{ padding: '13px', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Volver' : '返回'}
              </button>
              <button
                onClick={async () => {
                  const o = cancelConfirmOrder;
                  setCancelConfirmOrder(null);
                  await handleCancelOrder(o);
                }}
                style={{ padding: '13px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? '✅ Confirmar' : '✅ 确认取消'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 申诉模态框 ── */}
      {disputeOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <p style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '900', color: '#DC2626' }}>
              {lang === 'es' ? '⚠️ Abrir Disputa' : '⚠️ 发起申诉'}
            </p>
            <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>
              {lang === 'es'
                ? 'El pago quedará retenido hasta que el soporte resuelva el caso.'
                : '提交后平台将冻结资金，客服介入处理，请详细描述问题。'}
            </p>
            <textarea
              placeholder={lang === 'es' ? 'Describe el problema en detalle...' : '请详细描述问题（必填）...'}
              value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
              style={{ width: '100%', height: '100px', padding: '12px', borderRadius: '10px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '14px', resize: 'none', outline: 'none', marginBottom: '14px' }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                onClick={() => { setDisputeOrder(null); setDisputeReason(''); }}
                style={{ padding: '13px', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: 'pointer' }}>
                {lang === 'es' ? 'Cancelar' : '取消'}
              </button>
              <button
                disabled={!disputeReason.trim()}
                onClick={async () => {
                  const o = disputeOrder;
                  const r = disputeReason;
                  setDisputeOrder(null); setDisputeReason('');
                  await handleDispute(o, r);
                }}
                style={{ padding: '13px', background: disputeReason.trim() ? '#DC2626' : '#CBD5E1', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '900', fontSize: '14px', cursor: disputeReason.trim() ? 'pointer' : 'not-allowed' }}>
                {lang === 'es' ? '⚠️ Enviar' : '⚠️ 提交申诉'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
